import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import { useParams, useRouter } from "next/navigation";

import { getAllRelatedOrgsByUserId } from "@/actions/orgs";
import type { OrgModel } from "@/api";
import type { TreeItem } from "@/components/ui/tree-dropdown";
import { useLoggedUser } from "@/hooks/use-logged-user";
import type { TopNavigationProps, TreeNode } from "@/types";

const orgTreeAtom = atom<TreeNode[]>([]);

// Cache key for localStorage
export const ORG_TREE_CACHE_KEY = "sequelizer-org-tree-cache";

// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

function buildOrgTree(
  orgs: OrgModel[],
  parentId: string | null = null,
): TreeNode[] {
  const children = orgs
    .filter((org) => org.parentId === parentId)
    .map((org) => ({
      id: org.id,
      name: org.name || "Unnamed Organization",
      children: buildOrgTree(orgs, org.id),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return children;
}

// Helper function to convert TreeNode to TreeItem
export function treeNodesToTreeItems(nodes: TreeNode[]): TreeItem[] {
  return nodes.map((node) => ({
    id: node.id,
    name: node.name || "Unnamed Organization", // Ensure name is never undefined
    children: node.children ? treeNodesToTreeItems(node.children) : undefined,
  }));
}

const getOrgsQuery = async (userId: string, userOrgs: OrgModel[]) => {
  const relatedOrgs = await getAllRelatedOrgsByUserId(userId);

  // Combine user's direct orgs and related orgs; then remove duplicates and sort
  const list = [...userOrgs, ...((relatedOrgs || []) as OrgModel[])].filter(
    (org): org is OrgModel => !!org,
  );

  const combinedOrgs = Array.from(
    new Map(list.map((o) => [o.id, o])).values(),
  ).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  sessionStorage.setItem(
    ORG_TREE_CACHE_KEY,
    JSON.stringify({ orgs: combinedOrgs, timestamp: Date.now() }),
  );

  return combinedOrgs;
};

export const useOrgTree = (props: Readonly<TopNavigationProps>) => {
  const [treeData, setTreeData] = useAtom(orgTreeAtom);
  const { user } = useLoggedUser();
  const router = useRouter();
  const params = useParams();
  const orgId = params.orgId;
  const didSelectOnceRef = useRef(false);

  const localData =
    typeof window !== "undefined"
      ? sessionStorage.getItem(ORG_TREE_CACHE_KEY)
      : null;
  type OrgTreeCache = { orgs: OrgModel[]; timestamp: number };
  const parsedLocalData: OrgTreeCache | null = localData
    ? (JSON.parse(localData) as OrgTreeCache)
    : null;
  const isCacheValid =
    parsedLocalData &&
    parsedLocalData.timestamp &&
    Date.now() - parsedLocalData.timestamp < CACHE_EXPIRATION;

  const {
    data: orgs,
    isFetching,
    isLoading,
  } = useQuery({
    // include user id in key to scope cache per user
    queryKey: ["org-tree", user?.id],
    queryFn: () => {
      if (!user?.id) throw new Error("User ID is required");
      return getOrgsQuery(user.id, user.userOrgs || []);
    },
    // SWR: always fetch when user present; hydrate from cache if available
    enabled: !!user?.id,
    initialData: isCacheValid ? parsedLocalData?.orgs : undefined,
    // Ensure we fetch fresh data after onboarding redirect
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // Handle side effects when orgs data changes
  useEffect(() => {
    if (!orgs) return;

    if (!didSelectOnceRef.current && user?.userOrgs?.[0]) {
      props.onOrgSelect?.(user.userOrgs[0]);
      didSelectOnceRef.current = true;
    }

    if (orgs.length) {
      const orgTree = buildOrgTree(orgs);
      setTreeData(orgTree);
    } else {
      setTreeData([]);
    }
  }, [orgs, user?.userOrgs, setTreeData]);

  // Optionally re-hydrate from cache when valid (guards unnecessary updates)
  useEffect(() => {
    // Avoid rehydrating if we already have live data or an existing tree
    if (treeData.length || (orgs && orgs.length)) return;
    if (isCacheValid && parsedLocalData?.orgs?.length) {
      const orgTree = buildOrgTree(parsedLocalData.orgs);
      setTreeData(orgTree);
    }
  }, [
    isCacheValid,
    parsedLocalData?.timestamp,
    setTreeData,
    treeData.length,
    orgs?.length,
  ]);

  const findNodeById = (
    nodes: TreeNode[],
    id?: string,
  ): TreeNode | undefined => {
    for (const n of nodes) {
      if (n.id === id) return n;
      const hit = n.children ? findNodeById(n.children, id) : undefined;
      if (hit) return hit;
    }
    return undefined;
  };

  const selectedOrg = useMemo(
    () => findNodeById(treeData, orgId as string),
    [treeData, orgId],
  );
  const selectedOrgFlat = useMemo(
    () => orgs?.find((o) => o.id === (orgId as string)),
    [orgs, orgId],
  );
  const selectedOrgName = selectedOrg?.name ?? selectedOrgFlat?.name;
  const selectedOrgId =
    (orgId as string) || selectedOrg?.id || selectedOrgFlat?.id;

  const goToOrg = (selectedOrgId: string) => {
    // Save to localStorage immediately when org is selected
    try {
      localStorage.setItem("selected-org-id", selectedOrgId);
    } catch {
      // Ignore localStorage errors (e.g., private browsing mode)
    }

    const currentUrl = window.location.pathname;
    const newUrl = currentUrl.includes("/admin")
      ? `/${selectedOrgId}/admin`
      : `/${selectedOrgId}/dashboard`;
    router.push(newUrl);
  };

  return {
    treeData,
    allOrgs: orgs,
    // first subscription root exposed for separate rendering
    subscriptionItem: (() => {
      const ids = new Set((orgs || []).map((o) => o.id));
      const roots = (orgs || []).filter(
        (o) => !o.parentId || !ids.has(o.parentId as string),
      );
      const first = roots[0];
      return first
        ? ({
            id: first.id,
            name: first.name || "Unnamed Organization",
          } as TreeItem)
        : undefined;
    })(),
    // expose fetching state to consumers (shows spinner while refetching)
    isPending: isLoading || isFetching,
    selectedOrg,
    selectedOrgName,
    selectedOrgId,
    goToOrg,
  };
};
